import React, { ChangeEvent, useEffect } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  ClearButton,
  FaMagnifyingGlassIcon,
  HomeHeaderBox,
  NoResultFoundText,
  PatientListHeaderTitleGrid,
  SearchIconButton,
  SearchTextfield,
} from "../style/style";
import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  ReferralDetail,
  ReferralDetailsPayload,
  ReferralSearchPayload,
} from "../../../models/Referral";
import { getValue } from "../../../utils";
import AuthorizationDetails from "../containers/AuthorizationDetails";
import PatientList from "../containers/PatientList";
import { ApplicationRoute } from "../../../constants/ApplicationUrl";
import { numAlphabetSpaceRegex } from "../../../constants/Regex";

export interface PropsFromState {
  referralData: ReferralDetail[];
  referralSearchList: ReferralDetail[];
  setReferralData: React.Dispatch<React.SetStateAction<ReferralDetail[]>>;
  getValues: UseFormGetValues<ReferralSearchPayload>;
  showAuthHistory: number | null;
  setShowAuthHistory: (showAuthHistory: number | null) => void;
  resetReferralSearchList: () => void;
  setPage: (page: number) => void;
  setValue: UseFormSetValue<ReferralSearchPayload>;
  control: Control<ReferralSearchPayload, any>;
  listType: number;
  priorAuthButtonVisibility: boolean;
  agencyAccess: boolean;
}

export interface PropsFromDispatch {
  getReferralSearchList: (payload: ReferralSearchPayload) => void;
  getReferralListByProviderId: (payload: ReferralDetailsPayload) => void;
  resetReferralList: () => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const AllPatientList: React.FC<AllProps> = ({
  referralData,
  referralSearchList,
  getReferralSearchList,
  getReferralListByProviderId,
  setReferralData,
  getValues,
  showAuthHistory,
  setShowAuthHistory,
  resetReferralList,
  resetReferralSearchList,
  setPage,
  setValue,
  control,
  listType,
  agencyAccess
}) => {
  const navigate = useNavigate();
  useEffect(() => {
    setReferralData(referralSearchList);
  }, [referralSearchList]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue("searchTerm", e.target.value);
  };

  const handleSearch = () => {
    resetReferralSearchList();
    setPage(1);
    setValue("pageNo", 1);
    getReferralSearchList(getValues());
    resetReferralList();
  };

  const handleClear = () => {
    setValue("searchTerm", "");
    setPage(1);
    setValue("pageNo", 1);
    resetReferralSearchList();
    const payload = {
      providerId: getValues("providerId"),
      pageNo: getValues("pageNo"),
      pageSize: getValues("pageSize"),
    };
    getReferralListByProviderId(payload);
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const patientListHeader = () => {
    return (
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid sx={PatientListHeaderTitleGrid}>
          <Typography
            variant="h1"
            color={colors.black[17]}
            fontWeight={fontWeight[4]}
          >
            {listType === 0 ? "All Patients List" : "Patient List"}
          </Typography>
        </Grid>
        {listType === 0 && (
          <>
            <Grid sx={PatientListHeaderTitleGrid}>
              <Typography
                variant="h4"
                color={colors.black[17]}
                fontWeight={fontWeight[2]}
              >
                Total Patients: {getValue(referralData, "[0].totalRows", 0)}
              </Typography>
            </Grid>
            <Grid>
              <Controller
                render={(field) => (
                  <TextField
                    {...field}
                    id="outlined-basic"
                    variant="outlined"
                    size="small"
                    value={getValues("searchTerm")}
                    sx={SearchTextfield}
                    placeholder="Search"
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = target.value.replace(numAlphabetSpaceRegex, "");
                    }}
                  />
                )}
                name="searchTerm"
                control={control}
              />
              <Button sx={SearchIconButton} onClick={handleSearch}>
                <FaMagnifyingGlassIcon />
              </Button>
              <Button sx={ClearButton} onClick={handleClear}>
                Clear
              </Button>
            </Grid>
            <Grid>
              {agencyAccess && (
                <Button variant="contained" onClick={() => navigate(ApplicationRoute.MEMBER_INFO)}>Create Prior-Authorization</Button>
              )}
            </Grid>
          </>
        )}
      </Grid>
    );
  };
  return (
    <Box sx={HomeHeaderBox}>
      {patientListHeader()}
      {getValue(referralData, "[0].referralId") > 0 ? (
        <Grid container pt={2}>
          {listType === 0 ? (
            <AuthorizationDetails
              referralData={referralData}
              showAuthHistory={showAuthHistory}
              setShowAuthHistory={setShowAuthHistory}
              providerId={getValues("providerId")}
            />
          ) : (
            <PatientList
              referralData={referralData}
            />
          )}
        </Grid>
      ) : (
        <Typography
          variant="h3"
          color={colors.black[17]}
          fontWeight={fontWeight[2]}
          sx={NoResultFoundText}
        >
          No results found
        </Typography>
      )}
    </Box>
  );
};

export default AllPatientList;
